// Self-verifying FHA member-record certificate (H6). Built in the browser from the
// get_member_record RPC payload (via fetchMemberRecord) — no backend, no storage.
// jsPDF is lazy-loaded so its ~350KB only downloads when someone exports.
//
// Branding: EsuSol / ControlPilot navy + gold on a LIGHT print page, single-sourced
// from BRAND_LIGHT (shared with the transactional emails) so the certificate matches
// the emails members already receive.
//
// CERTIFIED-ONLY + FRESH: the export requires (1) a complete organizer name, (2) an
// existing organizer attestation, and (3) that attestation being CURRENT (the stored
// contribution digest matches the record now — Stage 37 attestation.current). The
// on-screen view is allowed un-attested; this PDF is not. The integrity fingerprint
// (record_sha256) covers the CONTRIBUTION entries (all statuses); disbursements
// received are shown for context and are not covered by that hash.
import { BRAND_LIGHT as C } from '../constants/brandColors';
import { groupByCycle } from './memberRecord';

const rgb = (hex) => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const EMPTY_SHA = 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855'; // sha256('')

// Certificate status wording. 'covered'/'paid' -> 'Confirmed' (per spec); pending -> 'Pending'.
const CERT_STATUS = {
  paid: 'Confirmed', covered: 'Confirmed', pending: 'Pending',
  member_submitted: 'Pending', rejected: 'Rejected', late: 'Late', voided: 'Voided',
};
const certStatus = (s) => CERT_STATUS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');
const statusColor = (label) => label === 'Confirmed' ? C.success : label === 'Pending' ? C.warning : C.heading;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const safe = (s) => String(s || 'record').replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'record';

export async function downloadMemberRecordPdf(record, { groupName } = {}) {
  if (!record?.organizer?.complete) {
    throw new Error('The group organizer must set their full legal name (Group Settings) before this record can be certified.');
  }
  if (!record?.attestation) {
    throw new Error('This record is not yet certified by the organizer.');
  }
  if (record.attestation.current === false) {
    throw new Error('This record changed since it was certified — ask your organizer to re-attest.');
  }

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const margin = 40;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => { if (y + needed > pageH - margin) { doc.addPage(); y = margin; } };
  const write = (text, { size = 11, style = 'normal', gap = 8, lineH = 15, color = C.heading } = {}) => {
    doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(...rgb(color));
    for (const line of doc.splitTextToSize(String(text), maxW)) {
      ensureSpace(lineH); doc.text(line, margin, y); y += lineH;
    }
    y += gap;
  };
  const sectionHeader = (title) => {
    y += 4; ensureSpace(26);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...rgb(C.heading));
    doc.text(title, margin, y);
    const w = doc.getTextWidth(title);
    y += 5;
    doc.setDrawColor(...rgb(C.gold)); doc.setLineWidth(1.5);
    doc.line(margin, y, margin + Math.max(w, 60), y); doc.setLineWidth(1);
    y += 14;
  };
  const cycleSubhead = (cycle, subtotal) => {
    y += 2; ensureSpace(16);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(...rgb(C.navy));
    doc.text(`Cycle ${cycle ?? '—'}`, margin, y);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...rgb(C.muted));
    doc.text(`subtotal ${money(subtotal)}`, pageW - margin, y, { align: 'right' });
    y += 12;
  };

  // Columns: Round | Date | Counterparty | Reference | Amount(right) | Status
  const COLS = [
    { x: margin,       w: 42,  label: 'Round',  align: 'left'  },
    { x: margin + 42,  w: 82,  label: 'Date',   align: 'left'  },
    { x: margin + 124, w: 132, label: null,     align: 'left'  },
    { x: margin + 256, w: 118, label: 'Ref.',   align: 'left'  },
    { x: margin + 374, w: 78,  label: 'Amount', align: 'right' },
    { x: margin + 452, w: 80,  label: 'Status', align: 'left'  },
  ];
  const clip = (s, w, size) => {
    let t = String(s ?? '—');
    while (t.length > 4 && doc.getStringUnitWidth(t) * size > w) t = t.slice(0, -2);
    return t === String(s ?? '—') ? t : t.replace(/.$/, '…');
  };
  const cellText = (val, c, baseY) => {
    const t = clip(val, c.w - 6, 9);
    if (c.align === 'right') doc.text(t, c.x + c.w - 3, baseY, { align: 'right' });
    else doc.text(t, c.x + 3, baseY);
  };
  const table = (counterpartyLabel, rows) => {
    const headH = 16;
    ensureSpace(headH + 14);
    // Header row: navy fill + white text.
    doc.setFillColor(...rgb(C.navy)); doc.rect(margin, y - 11, maxW, headH, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...rgb(C.white));
    COLS.forEach(c => { const lbl = c.label === null ? counterpartyLabel : c.label; if (lbl) cellText(lbl, c, y); });
    y += headH - 3;
    // Body rows on white, light rule under each.
    doc.setFontSize(9);
    for (const r of rows) {
      ensureSpace(15);
      const label = certStatus(r.status);
      const cells = [
        r.round_number != null ? String(r.round_number) : '—',
        fmtDate(r.date), r.counterparty || '—', r.reference || '—', money(r.amount), label,
      ];
      const rowY = y;
      for (let i = 0; i < COLS.length; i++) {
        if (i === 5) { doc.setFont('helvetica', 'bold'); doc.setTextColor(...rgb(statusColor(label))); }
        else { doc.setFont('helvetica', 'normal'); doc.setTextColor(...rgb(i === 1 ? C.muted : C.heading)); }
        cellText(cells[i], COLS[i], rowY);
      }
      y += 5;
      doc.setDrawColor(...rgb(C.border)); doc.setLineWidth(0.5); doc.line(margin, y, pageW - margin, y); doc.setLineWidth(1);
      y += 11;
    }
    y += 8;
  };

  // ── Header band: navy bg, white title, pale-gold subtitle, gold accent rule ──
  const bandH = 84;
  doc.setFillColor(...rgb(C.navy)); doc.rect(0, 0, pageW, bandH, 'F');
  doc.setTextColor(...rgb(C.white)); doc.setFont('helvetica', 'bold'); doc.setFontSize(21);
  doc.text('EsuSol', margin, 34);
  doc.setFontSize(14); doc.text('Member Savings-Club Record', margin, 57);
  doc.setTextColor(...rgb(C.paleGold)); doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text('Contribution & Collection Certificate', margin, 73);
  doc.setDrawColor(...rgb(C.gold)); doc.setLineWidth(2.5); doc.line(0, bandH, pageW, bandH); doc.setLineWidth(1);
  y = bandH + 26;

  // ── Member & organizer ───────────────────────────────────────────────
  write(`Group: ${groupName || '—'}     Generated: ${fmtDate(new Date().toISOString())}`, { size: 9, gap: 8, color: C.muted });
  write(`Member: ${record.member?.name || '—'}`, { size: 13, style: 'bold', gap: 2, lineH: 17 });
  write(`Organizer (attesting party): ${record.organizer?.name || '—'}`, { size: 10, gap: 2, color: C.heading });
  write(`Payment model: ${record.model === 'through_admin' ? 'Contributions handled through the organizer' : 'Contributions paid directly to the collector'}`,
    { size: 9, gap: 6, color: C.muted });

  const contributed = record.contributed || [];
  const received = record.received || [];
  const totalContrib = contributed.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalRecv = received.reduce((s, r) => s + Number(r.amount || 0), 0);
  const contribEmpty = contributed.length === 0 || record.attestation.record_sha256 === EMPTY_SHA;

  // ── Contributions made (grouped by cycle) ────────────────────────────
  sectionHeader(`Contributions made — ${contributed.length} · total ${money(totalContrib)}`);
  if (contribEmpty) {
    write('No contributions recorded as of the attestation date.', { size: 10, style: 'italic', gap: 8, color: C.muted });
  } else {
    for (const g of groupByCycle(contributed)) {
      cycleSubhead(g.cycle, g.subtotal);
      table('Paid to', g.rows.map(r => ({ ...r, counterparty: r.paid_to })));
    }
  }

  // ── Received / disbursements (grouped by cycle) ──────────────────────
  sectionHeader(`Received as collector — ${received.length} · total ${money(totalRecv)}`);
  if (received.length) {
    for (const g of groupByCycle(received)) {
      cycleSubhead(g.cycle, g.subtotal);
      table('Received from', g.rows.map(r => ({ ...r, counterparty: r.from })));
    }
  } else {
    write('No disbursements recorded.', { size: 10, style: 'italic', gap: 8, color: C.muted });
  }

  // ── Organizer attestation ────────────────────────────────────────────
  const a = record.attestation;
  sectionHeader('Organizer Attestation');
  write(`"${a.statement}"`, { size: 10, style: 'italic', gap: 8, color: C.heading });
  write(`Attested by ${a.attested_by_name || record.organizer?.name || '—'} on ${fmtDate(a.attested_at)} (statement version ${a.statement_version || '—'}).`,
    { size: 9, gap: 8, color: C.muted });

  // ── Integrity certification / fingerprint ────────────────────────────
  sectionHeader('Integrity Certification');
  write('These entries are maintained in a tamper-evident ledger: every change is recorded in an append-only audit log with a bound actor and server timestamp, so records cannot be altered or deleted without leaving a trace.',
    { size: 9, gap: 8, color: C.heading });
  write(`This fingerprint covers the member's contribution entries (all statuses) as attested on ${fmtDate(a.attested_at)}. Disbursements received are shown for context and are not covered by this fingerprint.`,
    { size: 9, style: 'italic', gap: 8, color: C.muted });
  const fp = [
    `Attestation ID: ${a.attestation_id ?? '—'}${a.digest_algo_version ? `  ·  algorithm ${a.digest_algo_version}` : ''}`,
    contribEmpty
      ? 'Contribution-record hash: none (no contributions as of the attestation date)'
      : `Contribution-record hash (SHA-256): ${a.record_sha256 || '—'}`,
    `Statement hash (SHA-256): ${a.statement_sha256 || '—'}`,
  ];
  for (const line of fp) write(line, { size: 8, style: 'normal', gap: 3, lineH: 11, color: C.heading });

  // ── Footer (all pages): navy rule + brand line ───────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...rgb(C.navy)); doc.setLineWidth(1.5);
    doc.line(margin, pageH - 34, pageW - margin, pageH - 34); doc.setLineWidth(1);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...rgb(C.navy));
    doc.text('EsuSol · a ControlPilot Systems product', margin, pageH - 22);
    doc.setFont('helvetica', 'italic'); doc.setTextColor(...rgb(C.gold));
    doc.text('Secure. Simplify. Scale.', margin, pageH - 12);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...rgb(C.footnote));
    doc.text(`Page ${p} of ${pages}`, pageW - margin - 54, pageH - 12);
  }

  doc.save(`esusol-record-${safe(record.member?.name)}.pdf`);
}
